import { CommunicationSizeComponent, ComponentIds } from '@inter-ikea-core/core';
import { useCore } from 'contexts/core';
import { store } from 'ducks';
import replaceFrame from 'rangeConfig/billy/behaviours/replaceFrame';
import { findMatchingFrameWithDifferentDepth } from 'rangeConfig/billy/utils/findMatchingFrameWithDifferentDepth';
import { useDispatch } from 'react-redux';
import { getProductFromEntity } from 'rangeConfig/shared/utils/getProductFromEntity';
import { useCallback, useEffect, useState } from 'react';
import SizeToggle from 'ui/SizeToggle/SizeToggle';
import { adjustMeasurementsSystem } from 'utils/adjustMeasurementsSystem';
import { useTranslations } from 'hooks/useTranslations';
import { useServiceSettings } from 'contexts/serviceSettings';

export const SolutionDepthToggle = () => {
  const coreApi = useCore();
  const { unit, useMetric } = useTranslations();
  const dispatch = useDispatch();
  const { translations } = useServiceSettings();

  const selectedFrame = coreApi.entities.findSelectedEntity();
  const products = store.getState().products.inMarket;

  const selectedFrameDimensions = selectedFrame?.getComponent<CommunicationSizeComponent>(
    ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
  )?.size;

  const [currentSize, setCurrentSize] = useState<number>(0);

  useEffect(() => {
    selectedFrameDimensions?.depth && setCurrentSize(selectedFrameDimensions?.depth);
  }, [selectedFrameDimensions]);

  const [depths, setDepths] = useState<{ depth: number; coreProductId: string }[]>([]);

  useEffect(() => {
    if (!selectedFrame) return;
    const currentFrame = getProductFromEntity(selectedFrame, products, coreApi);

    const matchingFrame = findMatchingFrameWithDifferentDepth(coreApi);

    const data = [currentFrame, matchingFrame]
      ?.filter(
        f =>
          typeof f?.communicatedDimensions?.depth === 'number' &&
          typeof f?.coreProductId === 'string'
      )
      ?.map(frame => ({
        depth: frame?.communicatedDimensions.depth as number,
        coreProductId: frame?.coreProductId as string
      }));

    if (data) {
      setDepths(data);
    }
  }, [coreApi, products, selectedFrame]);

  const handleSizeChange = useCallback(
    (newSize: number) => {
      const newCoreProductId = depths.find(d => d.depth === newSize)?.coreProductId;

      if (!selectedFrame || !newCoreProductId) return;
      setCurrentSize(newSize);

      replaceFrame({
        coreApi,
        rootState: store.getState(),
        props: { coreProductId: newCoreProductId },
        dispatch
      });
    },
    [dispatch, coreApi, depths, selectedFrame]
  );

  const sizes = depths
    ?.filter(d => d !== undefined)
    ?.sort((a, b) => a.depth - b.depth)
    ?.map(d => {
      const depth = adjustMeasurementsSystem(d.depth, translations, useMetric);

      return {
        value: d.depth ?? 0,
        text: `${depth}`,
        unit
      };
    });

  if (sizes?.length <= 0) return null;

  return (
    <SizeToggle sizes={sizes} currentSize={currentSize} onChangeSize={handleSizeChange} py="mdl" />
  );
};
